











































































import {
  isOverviewProgramChain,
  ProgramChain,
} from "@/data/models/ProgramChain";
import { Vue, Component, Prop, Model } from "vue-property-decorator";
import { IresDeviceDetailContent } from "@/components/devices/IresDeviceDetail.vue";

import StateButtonComponent, { ButtonState } from "../common/StateButton.vue";
import router from "@/router/router";
import { showProgramQualityRatingAccess, viewRunsAccess } from "@/permissions/UserPermissions";
import { getEffectiveUser } from "@/utils/ViewAs";

@Component({
  components: {
    "state-button": StateButtonComponent,
  },
})
export default class IresDeviceDetailContentSelectorComponent extends Vue {
  @Model("change")
  contentToDisplay!: IresDeviceDetailContent;

  @Prop() selectedProgramChain!: ProgramChain;
  @Prop({ default: false }) isCreatingDryingReport!: boolean;

  Content = IresDeviceDetailContent;

  /** The user whose view this shows: the chosen one while "Sicht auf" is active (ticket 2610052). */
  get activeUser() {
    return getEffectiveUser();
  }

  /** Phones (below 600 px): the view buttons are small. */
  get isPhone() {
    return this.$vuetify.breakpoint.xsOnly;
  }

  /** Phones and iPads (below 1264 px): the views as one segmented control. */
  get isCompact() {
    return this.$vuetify.breakpoint.mdAndDown;
  }

  /** The selected phone button: the view shown under the graph, Grafik for the graph alone. */
  get phoneSegment() {
    const views = [IresDeviceDetailContent.RUNS, IresDeviceDetailContent.RATE_DRYING, IresDeviceDetailContent.LOG];
    return views.includes(this.contentToDisplay) ? this.contentToDisplay : IresDeviceDetailContent.GRAPH;
  }

  /**
   * Green for the view shown, red for the others, as the desktop buttons. Qualität is disabled in the
   * combined view of several dryings, as on desktop: it is not a drying of its own, so ASSIST has no
   * prediction for it and answered "Chain 'overview' not found" (#2610011).
   */
  getPhoneViewState(view: IresDeviceDetailContent) {
    if (view === IresDeviceDetailContent.RATE_DRYING && isOverviewProgramChain(this.selectedProgramChain)) {
      return ButtonState.DISABLED;
    }
    return view === this.phoneSegment ? ButtonState.GREEN : ButtonState.RED;
  }

  /**
   * Same paths as the desktop buttons: leaving Qualität for Grafik closes the rating the way its
   * own toggle does (closeRateDrying); every other button switches the view directly, and the
   * view already shown stays as it is.
   */
  onPhoneViewSelected(view: IresDeviceDetailContent) {
    if (view === this.phoneSegment) {
      return;
    }

    if (view === IresDeviceDetailContent.GRAPH) {
      if (this.contentToDisplay === IresDeviceDetailContent.RATE_DRYING) {
        this.toggleRateDryingQuality();
      } else {
        this.$emit("change", IresDeviceDetailContent.GRAPH);
      }
      return;
    }

    this.updateDisplayAdditionalInfo(view);
  }

  get isAllowedToViewRuns() {
    return viewRunsAccess(this.activeUser);
  }

  get isAllowedToViewProgramQualityRating() {
    return showProgramQualityRatingAccess(this.activeUser);
  }

  get notesButtonText() {
    const notesButtonText = this.$t("panelDetail.actions.notes");
    const numNotes = this.selectedProgramChain?.numNotes ?? 0;

    if (numNotes) {
      return `${notesButtonText} (${numNotes})`;
    } else {
      return notesButtonText;
    }
  }

  get toggleDisplayRunsButtonState() {
    return this.getButtonColor(IresDeviceDetailContent.RUNS);
  }

  get toggleDisplayLogButtonState() {
    return this.getButtonColor(IresDeviceDetailContent.LOG);
  }

  get toggleRateDryingButtonState() {
    if (isOverviewProgramChain(this.selectedProgramChain)) {
      return ButtonState.DISABLED;
    }

    return this.getButtonColor(IresDeviceDetailContent.RATE_DRYING);
  }

  get toggleNotesButtonState() {
    return this.getButtonColor(IresDeviceDetailContent.PROGRAM_CHAIN_NOTES);
  }

  getButtonColor(info: IresDeviceDetailContent) {
    return info === this.contentToDisplay
      ? ButtonState.GREEN
      : ButtonState.RED;
  }

  toggleDisplayRuns() {
    this.updateDisplayAdditionalInfo(IresDeviceDetailContent.RUNS);
  }

  toggleDisplayLog() {
    this.updateDisplayAdditionalInfo(IresDeviceDetailContent.LOG);
  }

  toggleRateDryingQuality() {
    if (this.contentToDisplay === IresDeviceDetailContent.RATE_DRYING) {
      this.$emit("closeRateDrying");

      return;
    }

    this.updateDisplayAdditionalInfo(IresDeviceDetailContent.RATE_DRYING);
  }

  toggleDisplayProgramChainNotes() {
    this.updateDisplayAdditionalInfo(
      IresDeviceDetailContent.PROGRAM_CHAIN_NOTES
    );
  }

  updateDisplayAdditionalInfo(update: IresDeviceDetailContent) {
    this.$emit(
      "change",
      update === this.contentToDisplay ? IresDeviceDetailContent.GRAPH : update
    );
  }

  async onOpenDryingRating() {
    await router.push({
      path: "rateDrying",
      query: {
        serialNumber: this.selectedProgramChain.serialNumber,
        chainId: this.selectedProgramChain.id,
      },
    });
  }

  async onCreateDryingReport() {
    this.$emit("createDryingReport");
  }
}
